import PouchDB from 'pouchdb';
import { Product as ImportedProduct } from '@/utils/types/product';
import { NO_ALCOHOL_STORES } from '@/utils/constant';
import { searchBySingleIndex } from '@/services/algolia';
import { ALGOLIA_INDEXES } from '@/services/algolia/config';
import { getAlcoholStoreFilter } from '@/helpers/productsFilterHelper';

let productDb = new PouchDB('products_db');

type Product = ImportedProduct & {
  _rev?: string;
  _id?: string;
  lastUpdated?: number; // Add lastUpdated field
};

const includeFields: (keyof Product)[] = [
  "_rev",
  "_id",
  "articleNumber",
  "objectID",
  "brand",
  "categories",
  "channel",
  "countryOfOrigin",
  "createdAt",
  "description",
  "discount",
  "durableLifeCustomerShelfLife",
  "gtin",
  "images",
  "inStock",
  "inventoryLevel",
  "isMassOffers",
  "isVisible",
  "itemPackCount",
  "keywords",
  "linkeditem",
  "milkFatContentPercentageConcat",
  "milkFatContentRoundedOffPercentage",
  "moistureContentPercentageConcat",
  "moistureContentRoundedOffPercentage",
  "monounsaturatedPublishNutritional",
  "name",
  "nutritionalInformation",
  "objectID",
  "pageSlug",
  "price",
  "priceQuantity",
  "scaleLabelFixedWeight",
  "scaleLabelFixedWeightConcat",
  "scaleLabelFixedWeightUOM",
  "scenePlusPoints",
  "seasonedRequired",
  "storageInstructions",
  "storeId",
  "uom",
  "upc",
  "updatedAt",
  "weight",
  "promotions", // If this refers to a field — not shown in your sample object
  "lastUpdated", // You already had this
  "activePromotion", // Was missing
  "allergens",         // Was missing
  "hierarchicalCategories" // Was missing
];

type ProductDoc = Product & { _id: string; _rev?: string; lastUpdated: number };
type RowWithDoc = PouchDB.Core.AllDocsResponse<ProductDoc>["rows"][number] & {
  doc: ProductDoc;
  id: string;
};

function filterFields<T extends object>(obj: T, keys: (keyof T)[]): Partial<T> {
  const result: Partial<T> = {};
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined) {
      (result as Record<string, unknown>)[key as string] = value;
    }
  }
  return result;
}

async function normalizedId(ids: any[]){
 return Array.from(new Set(ids.map(id => id.split('_')[0]))
)}

async function deleteNonCompositeDocs() {
  const allDocs = await productDb.allDocs({ include_docs: true });

  // Just log total for safety
  console.log(`Total docs fetched: ${allDocs.rows.length}`);

  // Make sure we only delete docs whose ID does NOT contain "_"
  const docsToDelete = allDocs.rows
    .filter(doc => {
      const isComposite = doc.id.includes('_');
      if (!isComposite) {
        console.log(`Marking for deletion: ${doc.id}`);
      }
      return !isComposite && doc.value?.rev;
    })
    .map(async doc => ({
      _id: doc.id,
      _rev: doc.value.rev, // This comes from `include_docs: true`
      _deleted: true,
    }));

  if (docsToDelete.length === 0) {
    console.log("No docs to delete.");
    return;
  }

  try {
  const result = await productDb.bulkDocs(docsToDelete);
  console.log("Bulk delete result:", result);
  } catch (err) {
    console.error("Bulk delete failed:", err);
  }
}

export const addOrUpdateProductAndRefreshExisting = async (
  product: Product,
  // storeRegion: string,
  // storeID: string
) => {
  const store = localStorage.getItem("store");
  const storeID =  store && JSON.parse(store)?.storeNumber
  const storeRegion =  store && JSON.parse(store)?.region[0]
  const objectId = product?.objectID?.split('_').slice(0, 2).join('_')
  const docId = objectId;

  if (!docId) {
    console.error("Missing articleNumber, cannot update DB.");
    return;
  }

  try {
    // await deleteNonCompositeDocs();
    // 1. Get all existing product IDs from IndexedDB
    const allDocs = await productDb.allDocs({ include_docs: false });
    const existingIds = allDocs.rows.map((row: { id: any; }) => row.id);
   
    // 2. Include the current product ID as well
    const allIdsToUpdate = Array.from(new Set([...existingIds, docId]));
    // 3. Fetch fresh data from Algolia for ALL these IDs
    const freshProducts = await fetchAlgoliaProducts(allIdsToUpdate, storeRegion, storeID);
    // 4. Save all to IndexedDB (overwrites old ones)
    await saveProductsToDB(freshProducts);

    console.log(`Updated ${freshProducts.length} products from Algolia`);
  } catch (error) {
    console.error("Error in addOrUpdateProductAndRefreshExisting:", error);
  }
};

export const addOrUpdateProduct = async (product: Product) => {
  const docId = product.objectID;

  if (!docId) {
    console.error('Missing articleNumber, cannot add product to DB.');
    return;
  }

  const filtered = filterFields(product,includeFields);
  const doc = { ...filtered, _id: docId, lastUpdated: Date.now() }; // Added lastUpdated

  try {
    const existingProduct = await productDb.get(docId);
    await productDb.put({ ...doc, _rev: existingProduct._rev, lastUpdated: Date.now() }); // Added lastUpdated
  } 
  catch (error: any) {
    if (error.name === 'not_found') {
      await productDb.put(doc);
    } else {
      console.error('Error adding or updating product:', error);
    }
  }
};

// export const getBulkProductsFromDB = async (objectID: string[]) => {

//   const response = await productDb.allDocs({
//     include_docs: true,
//     keys: objectID,
//   });
//   const found: Product[] = [];
//   const missing: string[] = [];

//   response.rows.forEach((row) => {
//     if ('doc' in row && row.doc) {
//       const { ...rest } = row.doc;
//       const product: Product = {
//         ...(rest as Product),
//       };
//       found.push(product);
//     } else {
//       missing.push(row.key);
//     }
//   });

//   return { found, missing };
// };

export const getBulkProductsFromDB = async (objectIDs: string[], storeId: string) => {
  console.log(storeId , "checking")
  const response = await productDb.allDocs({
    include_docs: true,
    keys: objectIDs,
  });

  const found: Product[] = [];
  const missing: string[] = []; // Explicitly typed

  for (const row of response.rows) {
    if ('doc' in row && row.doc) {
      const product = row.doc as Product;

      console.log(product, "checking35")

      console.log(product.storeId, "checking34")
      if (product.storeId  == storeId) {
        found.push(product);
      } 
      else {
       missing.push(row.key);

      //   const updatedProduct = {
      //     ...product,
      //     stored: [...new Set([...storedStores, storeId])],
      //   };

      //   await productDb.put({
      //     ...updatedProduct,
      //     _id: product._id,
      //     _rev: product._rev,
      //   });
      }
    } else {
      missing.push(row.key); // ✅ Safe because missing: string[]
    }
  }


  return { found, missing };
};


function hasDoc(row: any): row is RowWithDoc {
  return !!row && typeof row.id === "string" && row.doc !== undefined && row.doc !== null;
}

export const saveProductsToDB = async (products: Product[]) => {
  try {
    const docsToPut = products
      .filter((p) => p.objectID?.split('_').slice(0, 2).join('_'))
      .map((product) => ({
        ...filterFields(product,includeFields),
        _id: product.objectID?.split('_').slice(0, 2).join('_'),
        lastUpdated: Date.now(), // Add lastUpdated here
      }));

    const existingDocsResult = await productDb.allDocs({ keys: docsToPut.map(doc => doc._id), include_docs: true });
    const existingDocsMap = new Map<string, ProductDoc>(
      existingDocsResult.rows
        .filter(hasDoc)
        .map((row) => [row.id as string, row.doc as ProductDoc])
    );

    const docsToBulkPut = docsToPut.map(doc => {
      const existingDoc = existingDocsMap.get(doc._id!);
      return existingDoc ? { ...doc, _rev: existingDoc._rev, lastUpdated: Date.now() } : doc; //and here
    });

    const result = await productDb.bulkDocs(docsToBulkPut);

    result.forEach((response: any) => {
      if ('error' in response) {
        console.error(`Error saving product ${docsToPut}:`, response);
      }
    });
  } catch (error) {
    console.error("Error saving products to DB:", error);
  }
};

async function fetchAlgoliaProducts(productIds: string[],storeRegion: string, storeID: string): Promise<ImportedProduct[]> {
  try {
    if (productIds.length === 0) {
      return []; // Return an empty array if no product IDs are provided
    }

    const itemsQuery = productIds.map(id => `objectID:${id}_${storeID}`).join(" OR ");

    // const filters = isNoAlcoholStore
    //   ? `(${itemsQuery}) AND storeId:${storeID} AND allergens.alcohol:false AND isMassOffers:false AND isVisible:true`
    //   : `(${itemsQuery}) AND storeId:${storeID} AND isMassOffers:false AND isVisible:true`;

    const filters = `(${itemsQuery}) AND ${getAlcoholStoreFilter(storeID, storeRegion)}`;
    // Replace this with your actual Algolia search call
    const algoliaResult = await searchBySingleIndex({ // Corrected function name
      indexName: ALGOLIA_INDEXES.PRODUCTS.PRIMARY_INDEX,
      searchParams: {
        filters,
        hitsPerPage: productIds.length,
      },
    });

    console.log("algoliaResult", algoliaResult, filters)
    // Assuming Algolia returns an object with a 'hits' property containing an array of products
    const products: ImportedProduct[] = algoliaResult?.hits || [];

    return products;
  } catch (error) {
    console.error('Error fetching products from Algolia:', error);
    return []; // Return an empty array on error
  }
};

export async function fetchDataWithFallback(
  ids: string[],
): Promise<ImportedProduct[]> {
  try {
    
    // await deleteNonCompositeDocs()
    if (!ids || ids.length === 0) {
      return [];
    }

    const store = localStorage.getItem("store");
    const storeID =  store && JSON.parse(store)?.storeNumber
    const storeRegion =  store && JSON.parse(store)?.region[0]
    const uniqueIds = [...new Set(ids)];
  

   const mergedIds = ids.map(id => `${id}_${storeID}`);
    // 1. Try to get from local DB
    const { found: foundProducts, missing: missingIds } = await getBulkProductsFromDB(uniqueIds,storeID);
    let finalProducts: ImportedProduct[] = [...foundProducts];

    const now = Date.now();
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;


    if (missingIds.length > 0) {
      // 2. Fetch missing products from Algolia
      try {
        // let articleIds = await normalizedId(missingIds);
        const algoliaProducts = await fetchAlgoliaProducts(missingIds, storeRegion, storeID);

        const productsToUpdate = algoliaProducts.filter(p => {
          const existingProduct = foundProducts.find(fp => fp.objectID === p.objectID?.split('_').slice(0, 2).join('_'));
          return !existingProduct || (now - (existingProduct.lastUpdated ?? 0)) > TWENTY_FOUR_HOURS;
        });
        // 3. Save to local DB
        await saveProductsToDB(productsToUpdate);

        // 4. Merge with existing
        finalProducts = [...finalProducts, ...algoliaProducts];
      } catch (err) {
        console.error("Error fetching data from Algolia:", err);
        // Consider whether to throw the error or return a partial result
      }
    }
    else{
       const productsToUpdate = foundProducts.filter(p => (now - (p.lastUpdated ?? 0)) > TWENTY_FOUR_HOURS);
       if(productsToUpdate.length > 0){
         try{
            const algoliaProducts = await fetchAlgoliaProducts(productsToUpdate.map(p=>p.objectID),storeRegion, storeID);
            await saveProductsToDB(algoliaProducts)
         }
         catch(error){
           console.error("Error fetching from algolia", error)
         }
       }
    }
    return finalProducts;
  } catch (error) {
    console.error("Error in fetchDataWithFallback:", error);
    return [];
  }
};

export const getProductBySlugFromDB = async (slug: string[]): Promise<Product | null> => {
  const allDocs = await productDb.allDocs({ include_docs: true });

  const match = allDocs.rows.find(row => {
    const doc = row.doc as Product; 
    return doc?.pageSlug === slug[0];
  });

  return match?.doc as Product ?? null;
};

export const destroyCartDB = async () => {
  try {
    await productDb.destroy();
    console.log("PouchDB cart database destroyed successfully");
    productDb = new PouchDB('products_db');
  } catch (error) {
    console.error("Failed to destroy PouchDB cart database:", error);
  }
};


export default productDb;